import { getConnectionInfo } from 'infrastructure/diagnostics/collectEnvironment';
import { ConnectionMonitor } from 'infrastructure/diagnostics/types';
import { StorageProvider } from './BlobStorageService';

interface LogPayloadData {
  // File information
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  contentType?: string;

  // Upload context
  storageProvider?: StorageProvider;
  uploadMethod?: string;
  uploadStep?: string;
  uploadDuration?: number;

  // Error information
  errorType?: string;
  errorName?: string;
  errorMessage?: string;

  // HTTP request/response details (when available)
  requestUrl?: string;
  statusCode?: number;
  statusText?: string;
  responseBody?: string;
  responseHeaders?: Record<string, string>;

  // Multipart upload specific
  uploadId?: string;
  partNumber?: number;
  totalParts?: number;
  batchNumber?: number;

  // Additional context
  objectKey?: string;
  presignedUrl?: string;

  // Browser/environment info
  userAgent?: string;
  event?: string;

  // Allow additional properties
  [key: string]: unknown;
}

interface HttpErrorDetails {
  requestUrl: string | undefined;
  statusCode: number | undefined;
  statusText: string | undefined;
  responseBody: string | undefined;
  responseHeaders: Record<string, string> | undefined;
}

const runtimeConsole = globalThis.console;

// Helper function to extract HTTP response details from errors
export const extractHttpErrorDetails = (error: unknown): HttpErrorDetails => {
  let requestUrl: string | undefined;
  let statusCode: number | undefined;
  let statusText: string | undefined;
  let responseBody: string | undefined;
  let responseHeaders: Record<string, string> | undefined;

  // Try to get details from Axios error response
  if (error && typeof error === 'object' && 'response' in error) {
    const { response } = error as any;
    if (response?.status) {
      statusCode = response.status;
      statusText = response.statusText;
      responseBody = response.data;
      responseHeaders = response.headers;
    }

    // Try to get the full request URL from Axios error config
    // Priority: error.config.url > response.config.url > request.responseURL
    if ('config' in error && (error as any).config?.url) {
      requestUrl = (error as any).config.url;
    } else if (response?.config?.url) {
      requestUrl = response.config.url;
    } else if (response?.request?.responseURL) {
      requestUrl = response.request.responseURL;
    }
  }

  // Handle XMLHttpRequest errors (they may have responseURL)
  if (
    !requestUrl &&
    error &&
    typeof error === 'object' &&
    'response' in error
  ) {
    const errorResponse = (error as any).response;
    if (errorResponse?.url) {
      requestUrl = errorResponse.url;
    }
  }

  // Fallback: extract from error message
  if (!statusCode && error instanceof Error) {
    const match = error.message.match(/status code (\d+)/i);
    if (match) statusCode = parseInt(match[1], 10);
  }

  return { requestUrl, statusCode, statusText, responseBody, responseHeaders };
};

// Helper function to create common error properties
export const createCommonErrorProperties = (
  fileName: string,
  file: { type: string; size: number },
  storageProvider: StorageProvider,
  errorType: string,
  error: unknown,
  additionalProps: Record<string, unknown> = {}
): LogPayloadData => {
  const { requestUrl, statusCode, statusText, responseBody, responseHeaders } =
    extractHttpErrorDetails(error);

  // Base error information
  const result: LogPayloadData = {
    // File information
    fileName,
    fileType: file.type,
    fileSize: file.size,

    // Upload context
    storageProvider,
    errorType,

    // Error information
    errorMessage: error instanceof Error ? error.message : 'Unknown error',
    errorName: error instanceof Error ? error.name : 'Unknown',
  };

  // Add HTTP request/response details if available
  if (requestUrl) result.requestUrl = requestUrl;
  if (statusCode !== undefined) result.statusCode = statusCode;
  if (statusText) result.statusText = statusText;
  if (responseBody) result.responseBody = responseBody;
  if (responseHeaders) result.responseHeaders = responseHeaders;

  // Add additional properties, avoiding duplicates
  Object.entries(additionalProps).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  });

  return result;
};

// Unified logging utility for upload operations
export const logError = (event: string, data: LogPayloadData): void => {
  // Only log in non-production environments
  if (process.env.NODE_ENV !== 'production') {
    const payload = {
      event,
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...data,
    };
    runtimeConsole?.error(`${event}:`, payload);
  }
};

export const logInfo = (event: string, data: LogPayloadData): void => {
  // Only log in non-production environments
  if (process.env.NODE_ENV !== 'production') {
    const payload = {
      event,
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...data,
    };
    runtimeConsole?.log(`${event}:`, payload);
  }
};

// Upload-specific error types for step tracking
export interface UploadErrorData {
  step: string;
  additionalContext: Record<string, unknown>;
}

export interface MultipartUploadErrorData extends UploadErrorData {
  uploadId?: string;
  partNumber?: number;
}

// Factory functions to create error objects
export const createUploadError = (
  message: string,
  step: string,
  additionalContext: Record<string, unknown> = {}
): Error & UploadErrorData => {
  const error = new Error(message) as Error & UploadErrorData;
  error.name = 'UploadError';
  error.step = step;
  error.additionalContext = additionalContext;
  return error;
};

export const createMultipartUploadError = (
  message: string,
  step: string,
  uploadId?: string,
  partNumber?: number,
  additionalContext: Record<string, unknown> = {}
): Error & MultipartUploadErrorData => {
  const error = new Error(message) as Error & MultipartUploadErrorData;
  error.name = 'MultipartUploadError';
  error.step = step;
  error.uploadId = uploadId;
  error.partNumber = partNumber;
  error.additionalContext = { uploadId, partNumber, ...additionalContext };
  return error;
};

/**
 * Creates multipart upload error with preserved response details from original error
 */
export const createMultipartErrorWithResponse = (
  originalError: unknown,
  message: string,
  step: string,
  uploadId?: string,
  partNumber?: number,
  additionalContext: Record<string, unknown> = {}
) => {
  const multipartError = createMultipartUploadError(
    message,
    step,
    uploadId,
    partNumber,
    additionalContext
  );

  const httpDetails = extractHttpErrorDetails(originalError);

  if (
    httpDetails.statusCode !== undefined ||
    httpDetails.responseBody ||
    httpDetails.statusText ||
    httpDetails.requestUrl
  ) {
    (multipartError as any).response = {
      status: httpDetails.statusCode,
      statusText: httpDetails.statusText,
      data: httpDetails.responseBody,
      headers: httpDetails.responseHeaders || {},
      url: httpDetails.requestUrl,
    };
  }

  return multipartError;
};

// Type guard for multipart upload error detection
export const isMultipartUploadError = (
  error: unknown
): error is Error & MultipartUploadErrorData =>
  error instanceof Error &&
  'step' in error &&
  'additionalContext' in error &&
  'uploadId' in error &&
  'partNumber' in error;

// Helper to create upload error properties with consistent structure
const createUploadErrorProperties = (
  file: File,
  provider: StorageProvider,
  error: unknown,
  additionalProps: Record<string, unknown> = {}
) => {
  // Extract step from custom error types
  let step = 'unknown';
  let additionalContext = {};

  if (isMultipartUploadError(error)) {
    step = error.step;
    additionalContext = error.additionalContext;
  }

  return createCommonErrorProperties(
    file.name,
    { type: file.type, size: file.size },
    provider,
    'upload_error',
    error,
    {
      uploadStep: step,
      ...additionalContext,
      ...additionalProps,
    }
  );
};

// Main logging function for upload errors
export const logUploadError = (
  event: string,
  file: File,
  provider: StorageProvider,
  error: unknown,
  additionalProps: Record<string, unknown> = {},
  trackAppInsightsError: (
    error: string,
    properties?: Record<string, any>
  ) => void
) => {
  const errorProperties = createUploadErrorProperties(
    file,
    provider,
    error,
    additionalProps
  );

  logError(event, errorProperties);
  trackAppInsightsError(event, errorProperties);
};

type XhrDiagnostics = {
  phase: 'error' | 'timeout' | 'abort';
  method: string;
  url: string;
  responseURL?: string;
  status: number;
  statusText: string;
  readyState: number;
  responseSnippet?: string;
  headers?: string;
  hadUploadProgress: boolean;
  uploadedBytes: number;
  corsLikely: boolean; // status===0 && !hadUploadProgress
  midstreamResetLikely: boolean; // status===0 && hadUploadProgress
  timing?: Partial<PerformanceResourceTiming> & { nextHopProtocol?: string };
  connectionInfo: ConnectionMonitor;
};

type XhrError = Error & { diagnostics: XhrDiagnostics };

export function buildXhrDiagnosticObject({
  hadUploadProgress,
  method,
  xhr,
  phase,
  url,
  uploadedBytes,
}: {
  hadUploadProgress: boolean;
  method: 'PUT';
  xhr: XMLHttpRequest;
  phase: 'error' | 'timeout' | 'abort';
  url: string;
  uploadedBytes: number;
}): XhrError {
  let headersStr: string | undefined;
  let responseSnippet: string | undefined;
  try {
    headersStr = xhr.getAllResponseHeaders() || undefined;
  } catch {
    // Ignore failures from XHR diagnostics extraction.
  }
  try {
    const txt = typeof xhr.responseText === 'string' ? xhr.responseText : '';
    responseSnippet = txt ? txt.slice(0, 512) : undefined;
  } catch {
    // Ignore failures from XHR diagnostics extraction.
  }

  // Performance timing (best-effort)
  let timing: XhrDiagnostics['timing'];
  try {
    const name = xhr.responseURL || url;
    const entries = performance.getEntriesByName(
      name,
      'resource'
    ) as PerformanceResourceTiming[];
    const last = entries?.[entries.length - 1];
    if (last) {
      timing = {
        nextHopProtocol: (last as any).nextHopProtocol,
        startTime: last.startTime,
        duration: last.duration,
        transferSize: last.transferSize,
        encodedBodySize: last.encodedBodySize,
        decodedBodySize: last.decodedBodySize,
      };
    }
  } catch {
    // Ignore failures from XHR performance timing extraction.
  }

  const diag: XhrDiagnostics = {
    phase,
    method,
    url,
    responseURL: xhr.responseURL || undefined,
    status: xhr.status,
    statusText: xhr.statusText,
    readyState: xhr.readyState,
    responseSnippet,
    headers: headersStr,
    hadUploadProgress,
    uploadedBytes,
    corsLikely: xhr.status === 0 && !hadUploadProgress,
    midstreamResetLikely: xhr.status === 0 && hadUploadProgress,
    timing,
    connectionInfo: getConnectionInfo(),
  };

  const err = new Error(
    `[XHR ${phase}] status=${xhr.status} readyState=${xhr.readyState} ` +
      `uploadProgress=${hadUploadProgress ? uploadedBytes : 0} url=${
        xhr.responseURL || url
      }`
  ) as XhrError;
  err.diagnostics = diag;
  return err;
}

export function isXhrError(error: unknown): error is XhrError {
  return error instanceof Error && 'diagnostics' in error;
}
